import type { MergeTagFilterId, ResolvedFilters } from "./filters";
import type { Surface } from "./surfaces";

/**
 * The merge tag catalog — the single source of truth for what tags exist on the
 * RJ2/senderer render context, which filters each supports, and the sample
 * value shown in an editor chip.
 *
 * The catalog is **editor-agnostic**. It models only what the backend renderer
 * actually provides; feature-specific authoring affordances (Text Composer
 * `{{ product_name }}` aliases, product-grid model resolution, etc.) live in the
 * consuming feature, never here.
 *
 * Canonical context, sourced from:
 * - `connect-api/internal/service/preview/renderer.go` (`buildTemplateData`)
 * - `senderer/pkg/renderer/renderer.go` (`DynamicDataToMap`, `productToMap`,
 *   `cartToMap`) and `senderer/pkg/domain/dynamic_data.go` (collection keys)
 *
 * Three availability tiers describe when a tag resolves to real data:
 * - `always`         — present on every send (recipient/store/subscription).
 * - `productContext` — only populated when the send carries product/tile data
 *                      (everything under `tile_data.*`).
 * - `emailContext`   — only resolves inside an email body (e.g. unsubscribe URL).
 * - `pageContext`    — only resolves on hosted subscriber pages (unsubscribe /
 *                      preferences), never inside a send.
 *
 * Deliberately omitted from the picker:
 * - `message_id` — available at render time but not a useful authoring tag.
 * - `site.brand_name` on send surfaces — present in the preview renderer only,
 *   not at send time; exposed to hosted pages via `pageContext`.
 */

export type MergeTagAvailability =
  | "always"
  | "productContext"
  | "emailContext"
  | "pageContext";

export interface MergeTagDefinition {
  /** The raw identifier inside `{{ }}`, e.g. `customer.first_name`. */
  identifier: string;
  /** Human-readable label, e.g. "First Name". */
  label: string;
  /** Sample preview value shown in the editor chip, e.g. "Jane". */
  preview: string;
  /**
   * Picker-row secondary text. Defaults to the wire identifier; a host sets
   * it when something else is more useful to the author (e.g. the brand's
   * actual copy behind a generated-content tag).
   */
  secondary?: string;
  /** Id of the {@link MergeTagCategory} this tag belongs to. */
  categoryId: string;
  /** When this tag resolves to real data. Drives per-surface exposure. */
  availability: MergeTagAvailability;
  /** Filters the chip popover offers for this tag, in declaration order. */
  filters: MergeTagFilterId[];
  /** Filters enabled by default when the tag is first inserted. */
  defaultFilters?: ResolvedFilters;
  /**
   * Static DTL this tag must serialize to at send time (e.g. `{% now "Y" %}`).
   *
   * NOTE: `build()` does not read this — the editor value round-trips as the
   * plain `{{identifier}}` form (so auto-detect can re-chip it on reload). The
   * consumer substitutes `staticDtl` at the payload boundary, before handing the
   * body to the renderer (see `replaceStaticDtlMergeTags` in TextComposerV2).
   */
  staticDtl?: string;
  /** Whether this tag returns text or a URL. Used by features to filter for URL-type contexts. */
  tagType: "text" | "url";

  // True when the value is a money amount.
  monetary?: boolean;
}

export interface MergeTagCategory {
  id: string;
  label: string;
  tags: MergeTagDefinition[];
}

/**
 * Which availability tiers each surface exposes. Surfaces declare capability
 * here rather than each tag listing its surfaces, so adding a surface (or
 * changing what a surface supports) is a single edit.
 *
 * `subjectPreheader` is intentionally conservative: a subject line renders once
 * with no product/tile context, so only `always` tags are offered.
 */
const SURFACE_AVAILABILITY: Record<Surface, readonly MergeTagAvailability[]> = {
  textComposer: ["always", "productContext"],
  subjectPreheader: ["always", "emailContext"],
  grapesEmail: ["always", "productContext", "emailContext"],
  pageUnsubscribe: ["pageContext"],
};

const recipientTags: MergeTagDefinition[] = [
  {
    identifier: "customer.first_name",
    label: "First Name",
    preview: "Jane",
    categoryId: "recipient",
    availability: "always",
    filters: ["textCase", "default"],
    defaultFilters: { textCase: "capfirst" },
    tagType: "text",
  },
  {
    identifier: "customer.last_name",
    label: "Last Name",
    preview: "Johnson",
    categoryId: "recipient",
    availability: "always",
    filters: ["textCase", "default"],
    defaultFilters: { textCase: "capfirst" },
    tagType: "text",
  },
  {
    identifier: "customer.email",
    label: "Email Address",
    preview: "jane@example.com",
    categoryId: "recipient",
    availability: "pageContext",
    filters: [],
    tagType: "text",
  },
];

const storeTags: MergeTagDefinition[] = [
  {
    identifier: "site.brand_name",
    label: "Brand Name",
    preview: "Acme",
    categoryId: "store",
    availability: "pageContext",
    filters: [],
    tagType: "text",
  },
  {
    identifier: "site.logo_url",
    label: "Logo",
    preview: "https://shop.example.com/logo.png",
    categoryId: "store",
    availability: "pageContext",
    filters: [],
    tagType: "url",
  },
  {
    identifier: "site.url",
    label: "Site URL",
    preview: "https://shop.example.com",
    categoryId: "store",
    availability: "always",
    filters: [],
    tagType: "url",
  },
  {
    identifier: "copy.support_email",
    label: "Support Email",
    preview: "support@example.com",
    categoryId: "store",
    availability: "always",
    filters: [],
    tagType: "text",
  },
  {
    identifier: "site.timezone",
    label: "Timezone",
    preview: "America/New_York",
    categoryId: "store",
    availability: "always",
    filters: [],
    tagType: "text",
  },
  {
    identifier: "site.currency",
    label: "Currency",
    preview: "USD",
    categoryId: "store",
    availability: "always",
    filters: [],
    tagType: "text",
  },
];

const subscriptionTags: MergeTagDefinition[] = [
  {
    identifier: "unsubscribe_url",
    label: "Unsubscribe URL",
    preview: "https://shop.example.com/unsubscribe",
    categoryId: "subscription",
    availability: "emailContext",
    filters: [],
    tagType: "url",
  },
  {
    identifier: "subscription_preferences_url",
    label: "Subscription Preferences",
    preview: "https://shop.example.com/preferences",
    categoryId: "subscription",
    availability: "emailContext",
    filters: [],
    tagType: "url",
  },
];

const dateTags: MergeTagDefinition[] = [
  {
    identifier: "current_year",
    label: "Current Year (YYYY)",
    preview: new Date().getFullYear().toString(),
    categoryId: "date",
    availability: "always",
    filters: [],
    staticDtl: `{% now "Y" %}`,
    tagType: "text",
  },
  {
    identifier: "current_date",
    label: "Current Date (Month D, YYYY)",
    // Matches `{% now "F j, Y" %}`, e.g. "June 7, 2026".
    preview: new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
    categoryId: "date",
    availability: "always",
    filters: [],
    staticDtl: `{% now "F j, Y" %}`,
    tagType: "text",
  },
  {
    identifier: "current_date_short",
    label: "Current Date (MM/DD/YYYY)",
    // Matches `{% now "m/d/Y" %}`, e.g. "06/07/2026".
    preview: new Date().toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }),
    categoryId: "date",
    availability: "always",
    filters: [],
    staticDtl: `{% now "m/d/Y" %}`,
    tagType: "text",
  },
];

const cartTags: MergeTagDefinition[] = [
  {
    identifier: "tile_data.cart.value",
    label: "Cart Value",
    preview: "129.99",
    categoryId: "cart",
    availability: "productContext",
    filters: ["default"],
    tagType: "text",
    monetary: true,
  },
  {
    identifier: "tile_data.cart.item_count",
    label: "Cart Item Count",
    preview: "3",
    categoryId: "cart",
    availability: "productContext",
    filters: ["default"],
    defaultFilters: { default: "0" },
    tagType: "text",
  },
  {
    identifier: "tile_data.return_url",
    label: "Cart URL",
    preview: "https://example.com/cart",
    categoryId: "cart",
    availability: "productContext",
    filters: [],
    tagType: "url",
  },
];

export const CATALOG: MergeTagCategory[] = [
  { id: "recipient", label: "Recipient", tags: recipientTags },
  { id: "store", label: "Store", tags: storeTags },
  { id: "subscription", label: "Subscription", tags: subscriptionTags },
  { id: "date", label: "Date", tags: dateTags },
  { id: "cart", label: "Cart", tags: cartTags },
];

const CATALOG_BY_IDENTIFIER = new Map<string, MergeTagDefinition>(
  CATALOG.flatMap((category) =>
    category.tags.map((tag) => [tag.identifier, tag] as const),
  ),
);

// ---------------------------------------------------------------------------
// Product field template (productContext)
//
// Product fields repeat across every product collection and across the indexed
// items within a collection, so the catalog models them once as a reusable
// template + a collection dimension rather than enumerating the full
// (collection × index × field) cross-product. A consuming editor composes the
// concrete identifier for its product-grid model via
// {@link productFieldIdentifier}.
// ---------------------------------------------------------------------------

export interface ProductFieldTemplate {
  /** Field key on a product, e.g. `name` in `tile_data.magic_items[0].name`. */
  field: string;
  /** Human-readable label, e.g. "Name". */
  label: string;
  /** Sample preview value shown in the editor chip. */
  preview: string;
  /** Filters the chip popover offers for this field. */
  filters: MergeTagFilterId[];
  /** Whether this field returns text or a URL. */
  tagType: "text" | "url";
  // True when the value is a money amount.
  monetary?: boolean;
  /** Filters enabled when the tag is first inserted in an email. */
  defaultFilters?: ResolvedFilters;
  /**
   * The connector feature the field's data comes from. Pickers offer it only
   * where that connector is installed; stored tags keep resolving either way.
   */
  requires?: "reviews";
  /**
   * The value is a number sent as "" or "0" when there is none: the tag
   * renders only when the value is above 0.
   */
  hideUnlessPositive?: boolean;
}

/** The full set of scalar fields on a product (`senderer` `productToMap`). */
export const PRODUCT_FIELDS: readonly ProductFieldTemplate[] = [
  {
    field: "name",
    label: "Name",
    preview: "Acme Sneakers",
    filters: ["textCase"],
    tagType: "text",
  },
  {
    field: "item_url",
    label: "Link",
    preview: "https://example.com/p/acme",
    filters: [],
    tagType: "url",
  },
  {
    field: "image_url",
    label: "Image URL",
    preview: "https://example.com/img/acme.jpg",
    filters: [],
    tagType: "url",
  },
  {
    field: "price",
    label: "Price",
    preview: "129.99",
    filters: [],
    tagType: "text",
    monetary: true,
  },
  {
    field: "price_before",
    label: "Compare-at Price",
    preview: "159.99",
    filters: [],
    tagType: "text",
    monetary: true,
  },
  // Whole-number percent off computed by the renderer from price_before and
  // price (senderer#137); "" when there is no drop. A bare number so it
  // composes with copy ("Save {{ savings_percent }}%").
  {
    field: "savings_percent",
    label: "Savings %",
    preview: "50",
    filters: [],
    tagType: "text",
  },
  {
    field: "description",
    label: "Description",
    preview: "Lightweight everyday trainers",
    filters: ["textCase"],
    tagType: "text",
  },
  {
    field: "short_description",
    label: "Short Description",
    preview: "Everyday trainers",
    filters: ["textCase"],
    tagType: "text",
  },
  {
    field: "qty",
    label: "Quantity",
    preview: "1",
    filters: [],
    tagType: "text",
  },
  {
    field: "total_price",
    label: "Total Price",
    preview: "259.98",
    filters: [],
    tagType: "text",
    monetary: true,
  },
  {
    field: "display_currency",
    label: "Currency",
    preview: "USD",
    filters: [],
    tagType: "text",
  },
  {
    field: "stock_quantity",
    label: "Stock Quantity",
    preview: "12",
    filters: [],
    tagType: "text",
  },
  {
    field: "stock_status",
    label: "Stock Status",
    preview: "in_stock",
    filters: [],
    tagType: "text",
  },
  // Totals across every review of the product (FOG-2692), sent on every
  // product whatever the grid's reviews config. One decimal ("4.7", "5.0");
  // a product with no reviews sends "" or "0.0" / "0".
  {
    field: "review_average_rating",
    label: "Average Rating",
    preview: "5.0",
    filters: ["ratingDisplay"],
    defaultFilters: { ratingDisplay: "stars" },
    tagType: "text",
    requires: "reviews",
    hideUnlessPositive: true,
  },
  {
    field: "review_count",
    label: "Review Count",
    preview: "116",
    filters: [],
    tagType: "text",
    requires: "reviews",
    hideUnlessPositive: true,
  },
  {
    field: "id",
    label: "Product ID",
    preview: "sku-12345",
    filters: [],
    tagType: "text",
  },
];

/**
 * A path to one product: `product`, `items.0`, `tile_data.source_item`,
 * `tile_data.magic_items[0]`, `tile_data.dynamic[0].items[0]`. Never
 * `tile_data.metadata` / `tile_data.event_data` or `customer`, whose
 * properties can share a product field's name.
 */
const PRODUCT_PATH_PATTERN =
  /^(?:product|items\.\d+|tile_data\.source_item|tile_data\.(?!metadata\b|event_data\b)[a-z_]+\[\d+\]|tile_data\.dynamic\[\d+\]\.items\[\d+\])$/;

/**
 * The product field an identifier reads (`items.0.review_count`,
 * `tile_data.magic_items[0].review_count`, `product.review_count`), or
 * undefined when it isn't a product path or names no product field.
 */
export function productFieldOf(
  identifier: string,
): ProductFieldTemplate | undefined {
  const trimmed = identifier.trim();
  const dot = trimmed.lastIndexOf(".");
  if (!PRODUCT_PATH_PATTERN.test(trimmed.slice(0, dot))) return undefined;
  const field = trimmed.slice(dot + 1);
  return PRODUCT_FIELDS.find((f) => f.field === field);
}

/**
 * Product collection keys from `senderer` `ProductCollectionsFromDynamicData`.
 * Each is an array of products under `tile_data.<collection>`.
 */
export const PRODUCT_COLLECTIONS: readonly string[] = [
  "cart_items",
  "items",
  "top_sellers_items",
  "trending_items",
  "sale_velocity_items",
  "compare_items",
  "cross_sell_items",
  "upsell_items",
  "because_you_bought_items",
  "because_you_viewed_items",
  "purchased_items",
  "magic_items",
  "new_arrivals_items",
  "price_drop_items",
  "back_in_stock_items",
  "low_stock_items",
  "browsed_items",
  "rebuy_items",
  "event_items",
];

/**
 * Builds a concrete product field identifier, e.g.
 * `productFieldIdentifier("magic_items", "name")` → `tile_data.magic_items[0].name`.
 * The `dynamic` grid uses a nested shape: `tile_data.dynamic[g].items[i].<field>`.
 */
export function productFieldIdentifier(
  collection: string,
  field: string,
  index = 0,
): string {
  return `tile_data.${collection}[${index}].${field}`;
}

export interface ListOptions {
  surface?: Surface;
  categoryId?: string;
  tagType?: "text" | "url";
}

/**
 * Returns the catalog grouped by category, filtered to a surface and/or
 * category. A surface filter keeps only tags whose {@link MergeTagAvailability}
 * the surface exposes (see {@link SURFACE_AVAILABILITY}). Empty categories are
 * pruned. The result is a fresh array structure so callers can safely
 * mutate/sort it.
 */
export function list(opts: ListOptions = {}): MergeTagCategory[] {
  const { surface, categoryId, tagType } = opts;
  const allowed = surface ? SURFACE_AVAILABILITY[surface] : null;
  const result: MergeTagCategory[] = [];

  for (const category of CATALOG) {
    if (categoryId !== undefined && category.id !== categoryId) continue;

    const tags = category.tags.filter(
      (tag) =>
        (allowed === null || allowed.includes(tag.availability)) &&
        (tagType === undefined || tag.tagType === tagType),
    );
    if (tags.length === 0) continue;

    result.push({ id: category.id, label: category.label, tags });
  }

  return result;
}

export interface FindOptions {
  surface?: Surface;
}

/**
 * Looks up a catalog definition by its identifier (the part inside `{{ }}`).
 * Without a surface, page-only tags are excluded so send-time consumers never
 * chip a tag that will not render; pass `surface` to scope to one context.
 */
export function find(
  identifier: string,
  opts: FindOptions = {},
): MergeTagDefinition | undefined {
  const tag = CATALOG_BY_IDENTIFIER.get(identifier.trim());
  if (tag === undefined) return undefined;
  if (opts.surface !== undefined) {
    return SURFACE_AVAILABILITY[opts.surface].includes(tag.availability)
      ? tag
      : undefined;
  }
  return tag.availability === "pageContext" ? undefined : tag;
}
